import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { context } from 'bookingbug-core-js';
import { environment } from '../../environments/environment';

/**
 * Civica eStore PRE-BASKET (book-after-payment) integration for Redbridge -
 * talks to bl-civica-api (`/payment/civica/pre-basket*`) with
 * `payment_provider: 'estore'`.
 *
 * Flow (mirrors the live Solihull Civica eStore pre-basket standard):
 *   1. preBasketInvoke - persists the booking intent to DynamoDB
 *      (bl_purchase_logs_{client}) and builds a Civica eStore basket
 *      (CreateImmediatePaymentBasket). Returns the gateway redirect `url`.
 *      NO JRNI booking exists at this point - only a basket + service item.
 *   2. The customer pays at the Civica eStore.
 *   3. Civica POSTs a payment notification to bl-civica-api, which forwards it
 *      to the n8n "Civica Post-Basket Verify" workflow. That workflow checks
 *      out the JRNI basket, links the extra items carried in `data.fees`, sets
 *      the booking price, pays the purchase and marks the row PAID.
 *   4. preBasketFind - polled by verify-payment on return until the row shows
 *      the JRNI purchase (data.purchase_id / status PAID).
 *
 * Contract notes:
 *   - `amounts` are POUNDS, one line carries the whole payable total; the
 *     itemised fees ride in `data.fees` so the worker can link each extra
 *     item and recompute the price server-side.
 *   - `company_id` MUST be the PARENT (token-holding) company;
 *     `child_company` is the location the booking sits under.
 *   - `basket` MUST be a blApi `basket-public` basket that already holds
 *     the service item - the worker checks out by this id.
 */
@Injectable({
  providedIn: 'root',
})
export class CivicaService {
  // bl-civica-api sits behind the same host as the rest of the bl API, so this
  // follows api.service.ts rather than the branch-pinned hardcoding the older
  // Capita/HeyCentric services use - that way develop and main can carry the
  // same file. Matches the n8n Customer Configuration `bl_civica_api` for this
  // tenant (staging.bookinglab.co.uk / api.bookinglab.co.uk).
  private url = environment.production
    ? 'https://api.bookinglab.co.uk'
    : 'https://staging.bookinglab.co.uk';
  private civicaUrlPath = '/payment/';

  constructor(private http: HttpClient) {}

  /**
   * Create the pre-basket row + Civica eStore basket and get the redirect URL.
   *
   * @param company        child/location company the booking sits under
   * @param service        selected JRNI service
   * @param basket         JRNI basket (its id is the pre-basket row PK)
   * @param serviceItems   basket service items (each id is a pre-basket row SK)
   * @param descriptions   one description per payment line
   * @param client         JRNI client/member
   * @param fundCode       fund code(s) for the payment line(s)
   * @param amounts        amount(s) in POUNDS - one per payment line
   * @param authToken      end-user JRNI auth token, used by the worker to check out
   * @param data           stored as `extra_items` on the row (fees, answers...)
   * @param slot           selected slot ({ start })
   * @param extraDuration  extra duration carried from the questions
   * @param journeyRoute   journey identifier (e.g. 'ceremony')
   * @param reference      free-text reference stored on the row
   * @param parentCompanyId PARENT (token-holding) company id
   * @param quantity       quantity per payment line
   * @param catalogueIds   Civica eStore catalogue id per payment line
   * @param email          payer email, forwarded to the eStore basket
   * @param phone          payer phone, forwarded to the eStore basket
   */
  async preBasketInvoke(
    company,
    service,
    basket,
    serviceItems,
    descriptions,
    client,
    fundCode,
    amounts,
    authToken,
    data,
    slot,
    extraDuration,
    journeyRoute,
    reference,
    parentCompanyId?,
    quantity?,
    catalogueIds?,
    email?,
    phone?
  ) {
    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = serviceItemString
        ? `${serviceItemString},${serviceItem.id}`
        : `${serviceItem.id}`;
    }

    const origin = `${window.location.protocol}//${window.location.hostname}${port}`;

    // Civica returns the browser here after paying; the path segments are what
    // the verify page polls /pre-basket/find with. The certificate-ordering
    // journey has its own verify page nested under the journey route.
    let returnUrl = '';
    if (journeyRoute == 'order_a_copy_certificate') {
      returnUrl = `${origin}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${origin}/payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    const body: any = {
      host: `${context.apiUrl}`,
      payment_provider: 'estore',
      // company_id MUST be the PARENT (token-holding) company; child_company is
      // the location the booking sits under. The post-payment worker mints its
      // client token from parent_company_id first.
      company_id:
        parentCompanyId === undefined ? `${company.id}` : `${parentCompanyId}`,
      child_company: `${company.id}`,
      service_id: `${service.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${returnUrl}`,
      // The ?cancelled=true marker lets the verify page tell a pre-payment
      // back-out (the "back to site" button on the Civica basket page lands
      // here) apart from a normal return, so it can clear the basket and
      // restart the journey rather than poll for a payment that never happened.
      back_url: `${returnUrl}?cancelled=true`,
      auth_token: `${authToken}`,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute,
      reference: `${reference}`,
    };

    if (email) {
      body.email = `${email}`;
    }
    if (phone) {
      body.phone = `${phone}`;
    }
    if (quantity) {
      body.quantity = quantity;
    }
    if (catalogueIds) {
      body.catalogue_ids = catalogueIds;
    }

    if (Array.isArray(amounts)) {
      body.amounts = amounts;
    } else {
      body.amount = `${amounts}`;
    }
    if (Array.isArray(fundCode)) {
      body.fund_codes = fundCode;
    } else {
      body.fund_code = `${fundCode}`;
    }

    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket`, body)
      .toPromise();
  }

  // Poll a pre-basket row back by basket + service item. The row's status
  // (INITIATED -> NEW -> BOOKING_MADE -> PAYMENT_RECORDED -> PAID, or a
  // FAILED/CANCELLED terminal) and data.purchase_id tell the verify page when
  // the backend worker has created the JRNI booking.
  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket/find`, body)
      .toPromise();
  }

  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data,
    };
    return this.http
      .post(`${this.url}${this.civicaUrlPath}civica/pre-basket/update`, body)
      .toPromise();
  }
}
